import {Accordion, AccordionDetails, AccordionSummary, Grid, Typography, useTheme} from "@mui/material";
import {FormAutocomplete, FormCustomSwitch, FormTextField} from "../../../../common/form-fields";
import {useTranslation} from "react-i18next";
import React, {useEffect} from "react";
import {FormRateModel} from "../../../../api/types/rate";
import {ExpandMore} from "@mui/icons-material";
import {useRecoilState} from "recoil";
import {rateAccordionExpandedState} from "../../../../state/ratesRecoil";
import {FormElementI, SelectElementModelFE} from "../../../../api/types/generic";
import {useFormContext} from "react-hook-form";
import {numberIntegerRegex} from "../../../../constants";
import {setFormNumberValue} from "../../../../utils";

const OtherDefinitionComponent = ({
                                      currency,
                                      unlimitedKm,
                                      includedKm,
                                      publicEl,
                                      lateTolerance,
                                      minimumDuration,
                                      maximumDuration,
                                      lateDropOffRate,
                                      bestPrice,
                                      dealer
                                  }: {
    currency: FormElementI<string>
    unlimitedKm: FormElementI<boolean>
    includedKm: FormElementI<number>
    publicEl: FormElementI<boolean>
    lateTolerance: FormElementI<string>
    minimumDuration: FormElementI<string>
    maximumDuration: FormElementI<string>
    lateDropOffRate: FormElementI<SelectElementModelFE>
    bestPrice: FormElementI<boolean>
    dealer: FormElementI<SelectElementModelFE>
}) => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Theme
    const theme = useTheme();

    // Use Form Context
    const {formState: {isSubmitting, errors}, register, getValues, setValue} = useFormContext<FormRateModel>();

    // Use Recoil
    const [otherDefinitionExpanded, setOtherDefinitionExpanded] = useRecoilState(rateAccordionExpandedState("otherDefinition"));

    // Use Effect
    useEffect(() => {
        if (!isSubmitting && errors.otherDefinition)
            setOtherDefinitionExpanded(true);
        // eslint-disable-next-line
    }, [isSubmitting]);

    return (
        <Accordion expanded={otherDefinitionExpanded}
                   onChange={(e, expanded) => setOtherDefinitionExpanded(expanded)}
        >
            <AccordionSummary expandIcon={<ExpandMore/>}>
                <Typography sx={{color: theme.palette.primary.dark}}>
                    {t("pricing:rate.form.labels.otherDefinition")}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {/* OTHER DEFINITION */}
                <Grid container rowSpacing={2} columnSpacing={3}>
                    <Grid item xs={12} md={6} sx={{display: "flex", alignItems: "flex-end"}}>
                        {/* UNLIMITED KM */}
                        <FormCustomSwitch
                            {...register("otherDefinition.unlimitedKm")}
                            label={`${t("pricing:rate.form.labels.otherDefinitionLabels.unlimitedKm")}`}
                            readOnly={unlimitedKm.readonly}
                            handleChange={unlimitedKm.handleChange as Function}
                        />
                    </Grid>
                    {/*<Grid item xs={12} md={6} sx={{display: "flex", alignItems: "flex-end"}}>*/}
                    {/*    /!* INCLUDED KM *!/*/}
                    {/*    <FormTextField*/}
                    {/*        {...register("otherDefinition.includedKm")}*/}
                    {/*        label={`${t("pricing:rate.form.labels.otherDefinitionLabels.includedKm")}`}*/}
                    {/*        readOnly={includedKm.readonly}*/}
                    {/*        type={"text"}*/}
                    {/*        handleChange={(value: string) => setFormNumberValue(value, setValue, "otherDefinition.includedKm", numberIntegerRegex)}*/}
                    {/*    />*/}
                    {/*</Grid>*/}
                    <Grid item xs={12} md={6}>
                        {/* CURRENCY */}
                        <FormAutocomplete
                            {...register("otherDefinition.currency")}
                            label={`${t("pricing:rate.form.labels.otherDefinitionLabels.currency")}`}
                            readOnly={currency.readonly}
                            options={currency.options || []}
                            handleChange={currency.handleChange as Function}
                            isOptionEqualToValue={(option, value) => option === value}
                        />
                    </Grid>
                    {/*<Grid item xs={ 12 } md={ 6 }>*/}
                    {/*    <FormAutocomplete*/}
                    {/*        { ...register("otherDefinition.dealer") }*/}
                    {/*        label={ `${ t("pricing:rate.form.labels.otherDefinitionLabels.tenant") }*` }*/}
                    {/*        readOnly={ dealer.readonly }*/}
                    {/*        options={ dealer.options || [] }*/}
                    {/*        handleChange={ dealer.handleChange as Function }*/}
                    {/*        isOptionEqualToValue={ (option, value) => option.code === value.code }*/}
                    {/*    />*/}
                    {/*</Grid>*/}
                    <Grid item xs={12} md={6} sx={{display: "flex", alignItems: "flex-end"}}>
                        {/* BEST PRICE */}
                        <FormCustomSwitch
                            {...register("otherDefinition.bestPrice")}
                            label={`${t("pricing:rate.form.labels.otherDefinitionLabels.bestPrice")}`}
                            readOnly={bestPrice.readonly}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormTextField
                            {...register("otherDefinition.lateTolerance")}
                            label={`${t("pricing:rate.form.labels.otherDefinitionLabels.lateTolerance")}`}
                            readOnly={lateTolerance.readonly}
                            type={"text"}
                            handleChange={(value: string) => setFormNumberValue(value, setValue, "otherDefinition.lateTolerance", numberIntegerRegex)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* MINIMUM DURATION */}
                        <FormTextField
                            {...register("otherDefinition.minimumDuration")}
                            label={`${t("pricing:rate.form.labels.otherDefinitionLabels.minimumDuration")}`}
                            readOnly={minimumDuration.readonly}
                            type={"text"}
                            handleChange={(value: string) => setFormNumberValue(value, setValue, "otherDefinition.minimumDuration", numberIntegerRegex)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* MAXIMUM DURATION */}
                        <FormTextField
                            {...register("otherDefinition.maximumDuration")}
                            label={`${t("pricing:rate.form.labels.otherDefinitionLabels.maximumDuration")}`}
                            readOnly={maximumDuration.readonly}
                            type={"text"}
                            handleChange={(value: string) => setFormNumberValue(value, setValue, "otherDefinition.maximumDuration", numberIntegerRegex)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* LATE DROP OFF RATE */}
                        <FormAutocomplete
                            {...register("otherDefinition.lateDropOffRate")}
                            label={`${t("pricing:rate.form.labels.otherDefinitionLabels.lateDropOffRate")}`}
                            readOnly={lateDropOffRate.readonly}
                            options={lateDropOffRate.options || []}
                            handleChange={lateDropOffRate.handleChange as Function}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{display: "flex", alignItems: "flex-end"}}>
                        {/* PUBLIC */}
                        <FormCustomSwitch
                            {...register("otherDefinition.visibility")}
                            label={`${t("pricing:rate.form.labels.otherDefinitionLabels.visibility")}`}
                            readOnly={publicEl.readonly}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

export default OtherDefinitionComponent;